import { isNumber, sum } from 'lodash';
import { Dinero } from './dinero';
import { roundTo2Decimals, roundToDecimals } from './helpers';

export type MoneyInput = number | Dinero.Dinero;

const currencyLocales: Partial<Record<Dinero.Currency, string>> = {
  AUD: 'en-AU',
  NZD: 'en-NZ',
};

/** Owns conversion, arithmetic and display of monetary amounts. */
export class Money {
  static from(
    amount: MoneyInput,
    currency: Dinero.Currency = 'AUD'
  ): Dinero.Dinero {
    if (isNumber(amount)) {
      if (isNaN(amount)) {
        // eslint-disable-next-line no-console
        console.warn('NaN passed to Money.from');
        return Dinero({ amount: 0, currency });
      }
      return Dinero({ amount: this.toCents(amount), currency });
    }
    if (amount.getCurrency() !== currency) {
      throw new Error('Currency mismatch');
    }
    return amount;
  }

  static amount(amount: MoneyInput): number {
    const cents = this.from(amount).getAmount();
    return this.fromCents(cents);
  }

  /**
   * Used to convert from dollars to cents
   * Rounds to numberOfDecimals, defaults to 2
   */
  static toCents(
    amountInDollars: number,
    numberOfDecimals: number = 2
  ): number {
    if (numberOfDecimals === 2) {
      const cents = roundTo2Decimals(amountInDollars) * 100;
      return Math.round(cents);
    }
    const cents = roundToDecimals(amountInDollars, numberOfDecimals) * 100;
    return parseFloat(cents.toFixed(numberOfDecimals - 2));
  }

  /**
   * Used to convert from cents to dollars
   */
  static fromCents(
    amountInCents: number,
    numberOfDecimals: number = 2
  ): number {
    return roundToDecimals(amountInCents / 100, numberOfDecimals);
  }

  static percentageOfCents(amountInCents: number, percentage: number): number {
    return Math.round(amountInCents * (percentage / 100));
  }

  static allocate(amount: MoneyInput, proportions: number[]): Dinero.Dinero[] {
    const safeProportions = toSafeProportions(proportions);
    if (!amount || !safeProportions.length) {
      return [];
    }
    try {
      return this.from(amount).allocate(safeProportions);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error allocating', { amount, proportions });
      throw error;
    }
  }

  static sum(amounts: MoneyInput[]): Dinero.Dinero {
    return amounts
      .map((amount) => this.from(amount))
      .reduce((acc, amount) => acc.add(amount), this.from(0));
  }

  static format(amount: MoneyInput): string {
    return this.from(amount).toFormat();
  }

  /**
   * Formats a cents amount using the currency's own locale, so a patient in
   * their home region sees the bare symbol they expect rather than a
   * country-qualified one.
   */
  static formatPlainCents(
    amountInCents: number,
    currency: Dinero.Currency
  ): string {
    return new Intl.NumberFormat(currencyLocales[currency] ?? 'en-AU', {
      style: 'currency',
      currency,
    }).format(amountInCents / 100);
  }

  /**
   * Formats a cents amount with the country-qualified symbol, for contexts
   * where two currencies could otherwise be mistaken for each other.
   */
  static formatQualifiedCents(
    amountInCents: number,
    currency: Dinero.Currency
  ): string {
    return Dinero({ amount: amountInCents, currency }).toFormat();
  }
}

function toSafeProportions(proportions: number[]): number[] {
  const noNegatives = proportions.map((proportion) =>
    proportion < 0 ? 0 : proportion
  );
  const total = sum(noNegatives);
  if (total === 0) {
    return noNegatives.map(() => 1);
  }
  return noNegatives;
}
